import { Component, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { ConnectionService, OfflineModeService, ViewportService, SyncOptionsModalComponent, SyncOption, StorageService, SyncProgressComponent, ConfirmService, UpdateService } from '@luzula/luzula-lib';
import { DatabaseConfigService } from '../database-config.service';
import { DataService } from '../data.service';
import { Command, NavbarService } from '../navbar.service';
import { AuthService as AuthorizationService } from '../auth.service';
import { ProjectService } from '../project.service';
import { ChangelogService } from '../changelog/changelog.service';
import { ChangelogComponent } from '../changelog/changelog.component';
import { of } from 'rxjs';
import pkg from 'package.json';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
    standalone: false,
    selector: 'bp-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.css'],

})
export class NavbarComponent {
	router = inject(Router);
	activeRoute = inject(ActivatedRoute);
	version = pkg.version;

	isOnline = navigator.onLine;
	showDetails = false;

	C = Command;
	dbConfig = inject(DatabaseConfigService);

	private updateTitle() {
		this.ns.title = (this.ns.componentTitle);
	}

	constructor(
		public aus: AuthorizationService,
		public ds: DataService,
		public ns: NavbarService,
		public cs: ConnectionService,
		public storage: StorageService,
		public us: UpdateService,
		public oms: OfflineModeService,
		public ps: ProjectService,
		public vps: ViewportService,
		public cls: ChangelogService,
		private modalService: NgbModal,
		private confirmService: ConfirmService) {

		cs.monitor().subscribe((isOnline: boolean) => {
			this.isOnline = isOnline;
			if (isOnline) this.us.checkForUpdates();
			this.updateTitle();
		});
		const detailsFunc = (url: string) => {
			if (url === '/bohrpunkte') {
				this.showDetails = true;
			}
			else {
				this.showDetails = false;
				this.updateTitle();
			}
		};
		this.router.events.subscribe(
			(event: any) => {
				if (event instanceof NavigationEnd) {
					detailsFunc(this.router.url);
				}
			}
		);
		detailsFunc(this.router.url);
	}

	canEdit() {
		if (this.oms.offlineMode()) {
			return of(true);
		}
		else {
			return of(this.ps.canI('write', 'points'));
		}
	}

	title(): string {
		this.updateTitle();
		return this.ns.getTitle();
	}

	async onHomeClick(event?: MouseEvent) {
		if (this.us.updateReady()) {
			const confirmed = await this.confirmService.confirm({
				title: 'Update verfügbar',
				message: 'Ein Update wurde heruntergeladen und ist bereit zur Installation.',
				confirmText: 'Jetzt aktualisieren',
				cancelText: 'Später',
				confirmClass: 'btn-primary'
			});
			if (confirmed) {
				this.us.activateUpdate();
			}
			return;
		}
		if (this.router.url === '/') {
			if (event && event.ctrlKey)
				this.storage.cleanUp();
			else if (this.oms.offlineMode())
				return; // Im Offline-Modus kein Reload - SW könnte alte Version ausliefern
			else
				window.location.reload();
		}
		else
			this.router.navigate(['/']);
	}
	
	onGpsHome() {
		this.ns.command(Command.gpsHome).emit();
	}

	multipleRoles(): boolean {
		return this.aus.getRoles()?.size > 1;
	}
	
	currentRole() {
		if (this.aus.currentRole)
			return this.aus.currentRole;
		else
			return this.aus.getRoles().values().next().value;
	}
	
	currentUser() {
		return this.aus.currentUser;
	}
	
	switchRole(role: string) {
		this.aus.switchRole(role);
	}
	
	onLoggedOut() {
		this.aus.onLoggedOut();
		// Aktives Projekt entladen beim expliziten Ausloggen
		this.ps.unloadCurrentProject();
	}
	
	openManual() {
		window.open('/assets/bpweb.pdf', '_blank');
	}
	
	activeFilter(): boolean {
		return !!this.storage.getSync('currentFilter');
	}

	hasProject(): boolean {
		return !!this.ps.project();
	}

	isDeveloper(): boolean {
		return this.ps.amI('entwickler');
	}

	isProjectManager(): boolean {
		return this.ps.amI('manager');
	}

	isGuest(): boolean {
		return this.aus.amI('gast');
	}

	/** Prüft ob Netzwerk im Online-Modus nicht verfügbar ist */
	networkUnavailable(): boolean {
		return !this.oms.userOfflineMode() && !this.oms.networkOnline();
	}

	async onExitOfflineMode(): Promise<void> {
		try {
			await this.ds.exitOfflineMode();
			await this.oms.setOfflineMode(false);
		} catch (error) {
			console.error('Fehler beim Beenden des Offline-Modus:', error);
		}
	}

	async onSync(): Promise<void> {
		if (!this.oms.offlineMode()) return;

		// Optionen-Dialog öffnen
		const modalRef = this.modalService.open(SyncOptionsModalComponent);
		modalRef.componentInstance.networkOnline = this.oms.networkOnline();
		let action: SyncOption;
		try {
			action = await modalRef.result;
		} catch {
			return; // Dialog wurde geschlossen
		}
		if (action === 'cancel') return;

		if (action === 'sync-online' || action === 'sync-offline') {
			// Kurz warten damit async Operationen (z.B. setProjectValue) abschließen können
			await new Promise(resolve => setTimeout(resolve, 100));

			// Prüfen ob überhaupt lokale Daten vorhanden sind (persistenter Check)
			const hasLocalPoints = await this.ds.arePointsDownloadedForProject(this.ps.project());
			const hasPendingProject = this.ps.hasPendingChanges();

			if (!hasLocalPoints && !hasPendingProject) {
				// Keine lokalen Daten - nur Info-Meldung, kein Progress-Dialog
				await this.confirmService.alert('Es gibt keine Änderungen zu synchronisieren.');
				if (action === 'sync-online') {
					await this.oms.setOfflineMode(false);
				}
				return;
			}

			// Sync-Status setzen bevor Modal geöffnet wird
			this.ds.isSyncing.set(true);

			// Sync-Fortschritt-Dialog öffnen
			const progressRef = this.modalService.open(SyncProgressComponent, {
				backdrop: 'static',
				keyboard: false
			});

			// Daten synchronisieren (lokal → remote)
			let projectSyncOk = false;
			let pointsSyncOk = false;
			try {
				projectSyncOk = await this.ps.syncProjectToRemote();
				pointsSyncOk = await this.ds.syncToRemote();
			} catch (error) {
				console.error('Sync-Fehler:', error);
			} finally {
				// isSyncing IMMER zurücksetzen, damit der Dialog schließbar wird
				this.ds.isSyncing.set(false);
			}

			// Warten bis Modal geschlossen wird
			await progressRef.result;

			if (!projectSyncOk || !pointsSyncOk) {
				await this.confirmService.error(
					'Beim Synchronisieren sind Fehler aufgetreten. '
					+ 'Möglicherweise war keine Serververbindung möglich. '
					+ 'Die lokalen Daten sind weiterhin vorhanden.'
				);
				return;
			}

			if (action === 'sync-online') {
				await this.oms.setOfflineMode(false);
			}
		} else if (action === 'discard') {
			// Nur Offline-Modus verlassen, Daten behalten für späteren Offline-Einsatz
			await this.oms.setOfflineMode(false);
		}
	}

	openChangelog(): void {
		this.modalService.open(ChangelogComponent);
	}
}
